import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Apollo } from 'apollo-angular';

import { CookiePreference } from '@ui/shared/model';
import { map, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators';

import { BaseState } from '../reducers';
import { CookieService } from '../../storage/cookies.service';
import { LocalStorageService } from '../../storage/local-storage.service';
// Not sure why we explicitly have to go for more specific imports, but if not we get a runtime error
import { WINDOW_REF } from '../../browser/window-ref.token';
import { systemDowntimeQuery } from '../gql-queries';
import { INFRASTRUCTURE_CONFIG } from '../../infrastructure-config.token';
import { ModalService } from '../../../components/legacy/modal/modal.service';
import { CustomCookieSettingsModalComponent } from '../../../components/legacy/cookie-banner/custom-modal/custom-cookie-settings-modal.component';
import { getCurrentLocale } from './app.selectors';
import {
  CHANGE_LOCALE,
  ChangeLocale,
  GET_PERFORMANCE_WARNING_CONFIRMATION,
  GET_SYSTEM_DOWNTIME,
  GetPerformanceWarningConfirmation,
  GetSystemDowntime,
  INIT_COOKIES_PREFERENCE,
  InitCookiesPreference,
  OPEN_COOKIE_SETTINGS_MODAL,
  OpenCookieSettingsModal,
  SET_COOKIES_PREFERENCE,
  SET_PERFORMANCE_WARNING_CONFIRMATION,
  SetCookiesPreference,
  SetCookiesPreferenceSuccess,
  SetCurrentLocale,
  SetPerformanceWarningConfirmation,
  UPDATE_APPLICATION_SUCCESS,
  UPDATE_COOKIE_TRACKING,
  UpdateApplicationSuccess,
  UpdateCookieTracking
} from './app.actions';

@Injectable()
export class AppEffects {
  private apollo = inject(Apollo);
  private actions$ = inject(Actions);
  private storage = inject(LocalStorageService);
  private store = inject<Store<BaseState>>(Store);
  private modalService = inject(ModalService);
  private cookieService = inject(CookieService);
  private infrastructureConfig = inject(INFRASTRUCTURE_CONFIG);
  private windowRef = inject(WINDOW_REF);

  getSystemDowntime = createEffect(
    () =>
      this.actions$.pipe(
        ofType<GetSystemDowntime>(GET_SYSTEM_DOWNTIME),
        switchMap(() =>
          this.apollo
            .query<{ systemDowntime: { isSystemDown: boolean } }>({
              query: systemDowntimeQuery,
              fetchPolicy: 'no-cache'
            })
            .pipe(
              map(response => {
                if (response.data.systemDowntime.isSystemDown)
                  this.windowRef.open(
                    'https://www.mieter.immomio.com/wartungsarbeiten',
                    '_self'
                  );
              })
            )
        )
      ),
    { dispatch: false }
  );

  initCookiesPreference$ = createEffect(() =>
    this.actions$.pipe(
      ofType<InitCookiesPreference>(INIT_COOKIES_PREFERENCE),
      map(
        () =>
          new SetCookiesPreference(
            this.storage.getItem<CookiePreference>(
              this.infrastructureConfig.storageKeys.cookiePreference
            )
          )
      )
    )
  );

  setCookiesPreference$ = createEffect(() =>
    this.actions$.pipe(
      ofType<SetCookiesPreference>(SET_COOKIES_PREFERENCE),
      mergeMap(({ cookiePreference }) => [
        new UpdateCookieTracking(cookiePreference),
        new SetCookiesPreferenceSuccess(cookiePreference)
      ])
    )
  );

  openCustomCookieSettingsModal$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<OpenCookieSettingsModal>(OPEN_COOKIE_SETTINGS_MODAL),
        tap(({ cookiePreference, isTenant }) => {
          this.modalService
            .open(CustomCookieSettingsModalComponent, {
              backdrop: 'static',
              keyboard: false,
              data: { cookiePreference, isTenant }
            })
            .onClose()
            .subscribe((payload: CookiePreference) =>
              this.store.dispatch(new SetCookiesPreference(payload))
            );
        })
      ),
    { dispatch: false }
  );

  getPerformanceWarningConfirmation$ = createEffect(() =>
    this.actions$.pipe(
      ofType<GetPerformanceWarningConfirmation>(
        GET_PERFORMANCE_WARNING_CONFIRMATION
      ),
      map(() => {
        const preference =
          this.storage.getItem<boolean>(
            this.infrastructureConfig.storageKeys
              .hasAcceptedPerformanceWarningConfirmation
          ) || false;

        return new SetPerformanceWarningConfirmation(preference);
      })
    )
  );

  setPerformanceWarningConfirmation$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<SetPerformanceWarningConfirmation>(
          SET_PERFORMANCE_WARNING_CONFIRMATION
        ),
        map(action =>
          this.storage.setItem(
            this.infrastructureConfig.storageKeys
              .hasAcceptedPerformanceWarningConfirmation,
            action.confirmation
          )
        )
      ),
    { dispatch: false }
  );

  changeLocale$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<ChangeLocale>(CHANGE_LOCALE),
        withLatestFrom(this.store.select(getCurrentLocale)),
        map(([{ locale }, currentLocale]) => {
          const href = this.windowRef.location.href.replace(
            `/${currentLocale}/`,
            `/${locale}/`
          );
          this.store.dispatch(new SetCurrentLocale(locale));

          this.windowRef.location.href = href;
        })
      ),
    { dispatch: false }
  );

  updateApplicationSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<UpdateApplicationSuccess>(UPDATE_APPLICATION_SUCCESS),
        tap(() => this.windowRef.location.reload())
      ),
    { dispatch: false }
  );

  updateCookieTracking$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<UpdateCookieTracking>(UPDATE_COOKIE_TRACKING),
        tap(({ cookiePreference }) => this.updateTracking(cookiePreference))
      ),
    { dispatch: false }
  );

  private updateTracking(payload: CookiePreference) {
    this.cookieService.savePreference(payload);
  }
}
